/**
 * Magnitude-aware number + unit formatting for reporting widgets.
 *
 * The registry stores values in canonical units (kg, km, kg CO2e, GBP,
 * m³). Raw numbers in those units quickly become unreadable —
 * "1,234,567 kg" reads worse than "1,235 tonnes", and "£12,345,678"
 * reads worse than "£12.3M". This module promotes the unit and scales
 * the value at display time, without changing what's stored.
 *
 * Per-column promotion (not per-cell): given an array of values for
 * one column, ``pickColumnDisplay`` picks the unit that makes the
 * *largest* value readable. All cells in the column then use that
 * unit so the column is self-consistent.
 */

export interface FormattedColumn {
  /** Unit string to show in the column header, e.g. "tonnes CO₂e". */
  displayUnit: string;
  /** Multiplier applied to each cell value (1 / target_factor). */
  scale: number;
  /** Per-cell formatter — number + unit, e.g. "12,816.35 tonnes CO₂e". */
  format: (value: number | null | undefined) => string;
  /** Number only (with any currency prefix), no trailing unit — e.g.
   * "12,816.35" or "£1.23". For places that render the unit separately
   * (KPI tiles) so it isn't shown twice. */
  formatNumber: (value: number | null | undefined) => string;
  /** The unit to render on its own beside the number. Empty when the
   * unit is already carried by a currency prefix (so it's never doubled). */
  unitSuffix: string;
}

interface PromotionRule {
  /** Match the canonical unit exactly. */
  canonical: string;
  /** Tiers: ascending threshold, the unit + scale to use above it. */
  tiers: { threshold: number; unit: string; scale: number }[];
  /** Prefix for currency-style display (e.g. "£"). */
  prefix?: string;
}

// Promotion rules per known canonical unit. When the column's max
// value crosses a threshold the column flips to the bigger unit.
// Tiers are tried in descending order — the highest applicable wins.
const PROMOTION_RULES: PromotionRule[] = [
  // Mass — kg → tonnes when ≥ 1000 kg (1 tonne)
  {
    canonical: 'kg',
    tiers: [{ threshold: 1000, unit: 'tonnes', scale: 1 / 1000 }],
  },
  {
    canonical: 'kg CO₂e',
    tiers: [{ threshold: 1000, unit: 'tonnes CO₂e', scale: 1 / 1000 }],
  },
  // Volume — m³ stays m³ (no good bigger SI unit in common use); just
  // format with separators
  // Distance — km is the canonical; miles is an opt-in presentation
  // unit for US/UK audiences. ``threshold: Infinity`` means auto-
  // promotion never triggers (a 5000 km supplier still reads as km
  // by default); the user picks "miles" from the value-pill
  // dropdown to override per widget.
  {
    canonical: 'km',
    tiers: [{ threshold: Infinity, unit: 'miles', scale: 0.621371 }],
  },
  // Currency — GBP / USD / EUR get K / M abbreviations
  {
    canonical: 'GBP (reporting currency)',
    prefix: '£',
    tiers: [
      { threshold: 1_000_000, unit: 'M', scale: 1 / 1_000_000 },
      { threshold: 1_000, unit: 'K', scale: 1 / 1_000 },
    ],
  },
];

/**
 * Strip the " — default" qualifier currency units carry when the org
 * hasn't configured a reporting currency (the backend catalog returns
 * e.g. "GBP — default" to signal the fallback). Reporting widgets
 * should show a clean code like "GBP" — the "default" hint is noise in
 * a chart (#167). Leaves all other unit strings untouched.
 */
export function cleanUnitLabel(unit: string | null | undefined): string {
  if (!unit) return '';
  return unit.replace(/\s*[—–-]\s*default\s*$/i, '').trim();
}

/**
 * Turn a developer-facing enum token into a user-facing label:
 * "on_track" → "On track", "in_progress" → "In progress",
 * "under_construction" → "Under construction", "voluntary" → "Voluntary".
 *
 * Only transforms tokens that read as machine values — all lowercase with
 * no spaces (snake_case or a single lowercase word). Anything that already
 * has an uppercase letter or a space (product/supplier names, codes like
 * "BRC"/"SBTi", "Beef and veal", ISO dates) is returned unchanged, so this
 * is safe to apply broadly across widgets.
 */
export function humanizeLabel(value: unknown): string {
  const s = String(value ?? '');
  if (!s || /[A-Z]/.test(s) || s.includes(' ')) return s;
  const spaced = s.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const NUMBER_RE = /^-?\d+(\.\d+)?$/;

function asNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && NUMBER_RE.test(v)) return Number(v);
  return null;
}

/** Convert string-ish or number-ish cells to numbers; null if not numeric. */
export function normaliseNumeric(values: unknown[]): number[] {
  const out: number[] = [];
  for (const v of values) {
    const n = asNumber(v);
    if (n !== null) out.push(n);
  }
  return out;
}

/** Available promotion options for a canonical unit — used by the
 * value-pill dropdown so users can lock the display to a specific
 * presentation unit instead of letting magnitude promotion choose. */
export interface DisplayUnitOption {
  /** Stable identifier — the unit suffix as rendered, e.g. "tonnes",
   * "K", "GBP". The empty string means "canonical (auto)". */
  key: string;
  /** Human label for the dropdown, e.g. "tonnes (×1000)". */
  label: string;
  scale: number;
  unit: string;
}

/** Enumerate the legal display units for a canonical unit. Always
 * includes the canonical unit first (no scaling). */
export function displayUnitOptions(
  canonicalUnit: string | null | undefined,
): DisplayUnitOption[] {
  const unit = cleanUnitLabel(canonicalUnit);
  const rule = PROMOTION_RULES.find((r) => r.canonical === unit);
  const options: DisplayUnitOption[] = [
    { key: unit, label: unit ? `${unit} (canonical)` : 'No unit', scale: 1, unit },
  ];
  if (rule) {
    // Tiers sorted ascending — present smaller scales first
    [...rule.tiers]
      .sort((a, b) => a.threshold - b.threshold)
      .forEach((tier) => {
        options.push({
          key: tier.unit,
          label: `${tier.unit} (×${(1 / tier.scale).toLocaleString()})`,
          scale: tier.scale,
          unit: tier.unit,
        });
      });
  }
  return options;
}

/**
 * Pick the display unit + scale for a column based on the largest
 * value. Falls back to the canonical unit for unknown unit strings.
 *
 * Pass ``forceUnit`` to override magnitude promotion — useful for the
 * "presentation unit" preference exposed in the widget builder. The
 * value must match one of ``displayUnitOptions``' keys.
 */
export function pickColumnDisplay(
  canonicalUnit: string | null | undefined,
  values: unknown[],
  forceUnit?: string | null,
  decimals: number = 2,
): FormattedColumn {
  // Clamp to a sane range; NaN/negative fall back to the 2dp default.
  const dp =
    Number.isFinite(decimals) && decimals >= 0
      ? Math.min(Math.round(decimals), 10)
      : 2;
  const unit = cleanUnitLabel(canonicalUnit);
  const rule = PROMOTION_RULES.find((r) => r.canonical === unit);
  const numbers = normaliseNumeric(values);
  const maxAbs = numbers.length === 0 ? 0 : Math.max(...numbers.map(Math.abs));

  // Default: keep canonical unit, scale=1, 2dp formatting with separators
  let displayUnit = unit;
  let scale = 1;

  if (forceUnit !== undefined && forceUnit !== null && forceUnit !== '') {
    // Explicit override — find a matching tier on the rule.
    if (forceUnit === unit) {
      // Canonical pinned — no scaling
      displayUnit = unit;
      scale = 1;
    } else {
      const tier = rule?.tiers.find((t) => t.unit === forceUnit);
      if (tier) {
        displayUnit = tier.unit;
        scale = tier.scale;
      }
      // If forceUnit doesn't match anything known, fall through to
      // automatic promotion. Better than rendering garbage.
    }
  } else if (rule) {
    // Walk tiers from highest threshold down — first match wins
    const sorted = [...rule.tiers].sort((a, b) => b.threshold - a.threshold);
    for (const tier of sorted) {
      if (maxAbs >= tier.threshold) {
        displayUnit = tier.unit;
        scale = tier.scale;
        break;
      }
    }
  }

  const prefix = rule?.prefix ?? '';

  // The scaled number only (locale-formatted, no unit/prefix); null for
  // non-numeric input.
  const numberBody = (raw: number | null | undefined): string | null => {
    if (raw === null || raw === undefined) return null;
    const n = asNumber(raw);
    if (n === null) return null;
    const scaled = n * scale;
    // Integer-clean values display without trailing zeros (e.g. counts stay
    // "1,596", not "1,596.00"); fractional values get the configured decimal
    // places + locale separators.
    return Number.isInteger(scaled)
      ? scaled.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : scaled.toLocaleString(undefined, {
          minimumFractionDigits: dp,
          maximumFractionDigits: dp,
        });
  };

  const format = (raw: number | null | undefined): string => {
    const body = numberBody(raw);
    if (body === null) return '—';
    if (prefix && displayUnit === unit) {
      // No promotion — render "£1,234.56" (prefix only, no suffix)
      return `${prefix}${body}`;
    }
    if (prefix) {
      // Currency abbreviation — render "£1.23M"
      return `${prefix}${body}${displayUnit}`;
    }
    return `${body} ${displayUnit}`.trim();
  };

  // Number only (+ currency prefix), no trailing unit — for KPI tiles
  // that render the unit separately (muted) so it isn't doubled up.
  const formatNumber = (raw: number | null | undefined): string => {
    const body = numberBody(raw);
    if (body === null) return '—';
    return prefix ? `${prefix}${body}` : body;
  };

  // The unit shown on its own beside the number. Empty for un-promoted
  // currency, where the "£" prefix already carries the unit.
  const unitSuffix = prefix && displayUnit === unit ? '' : displayUnit;

  return { displayUnit, scale, format, formatNumber, unitSuffix };
}
